"use client";
import { useId } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { createPaginationUrl } from "helpers/url";
import { numberFormatter } from "helpers/number-formatter";

import IconArrow from "assets/svg/chevron_left_arrow.svg";

import styles from "./styles.module.css";

const PaginationWrapper = ({
  total,
  current,
  showFirstItem,
  showLastItem,
  maximumPage,
  listingCount,
  url,
  totalListingLength,
  isEmlakjetLink,
  useLegacyPagination = false,
  title = "",
}) => {
  const id = useId();
  const isShowLastPage = current === maximumPage;
  const currentPage = current !== 0 ? current : 1;

  const shouldShowAllPages = maximumPage <= 6;

  const getPaginationNumbers = () => {
    if (shouldShowAllPages) {
      return Array.from({ length: maximumPage }, (_, i) => i + 1);
    }
    return total || [];
  };

  const paginationNumbers = getPaginationNumbers();

  const calculatedShowFirstItem = !shouldShowAllPages && showFirstItem;
  const calculatedShowLastItem = !shouldShowAllPages && showLastItem;

  const pageHref = (page) => {
    if (isEmlakjetLink) {
      const base = typeof url === "string" ? url : url?.pagedPath || url?.urlForFirstPage || "";
      return `${base}&page=${page}`;
    }

    if (useLegacyPagination) {
      const cleanBasePath = typeof url === "string" ? url.replace(/\/$/, "") : url?.basePath?.replace(/\/$/, "");
      const queryParams = typeof url === "object" && url?.queryParams ? url.queryParams.replace(/^\?/, "") : "";

      if (!cleanBasePath) return "#";

      const path = page === 1 ? `${cleanBasePath}/` : `${cleanBasePath}/${page}`;
      return queryParams ? `${path}?${queryParams}` : path;
    }

    return createPaginationUrl({ url, page });
  };

  const firstItemHref = pageHref(1);
  const lastItemHref = pageHref(maximumPage);
  const previousHref = pageHref(currentPage - 1);
  const nextHref = pageHref(currentPage + 1);

  const leftArrowClasses = classNames({
    [styles.leftArrow]: true,
    [styles.arrowDisabled]: current <= 1,
  });
  const rightArrowClasses = classNames({
    [styles.rightArrow]: true,
    [styles.arrowDisabled]: isShowLastPage || maximumPage < 2,
  });

  const pageCountInfo = () => {
    if (isShowLastPage)
      return `${numberFormatter(totalListingLength - listingCount + 1)} - ${numberFormatter(
        totalListingLength
      )} arasındaki ilanlar listeleniyor.`;
    const startingListing = (currentPage - 1) * listingCount + 1;
    const endListing = Math.min(currentPage * listingCount, totalListingLength);
    return `${numberFormatter(startingListing)} - ${numberFormatter(endListing)} arasındaki ilanlar listeleniyor.`;
  };

  const isPaginationWrapperVisible = !!totalListingLength && maximumPage > 1;

  return (
    <>
      {isPaginationWrapperVisible && (
        <div className={styles.wrapper}>
          <ul className={styles.list}>
            <li className={leftArrowClasses}>
              {current > 1 ? (
                <a
                  href={previousHref}
                  {...(title ? { title: current - 1 === 1 ? title : `${title} ${currentPage - 1}. Sayfa` } : {})}
                >
                  <IconArrow />
                </a>
              ) : (
                <IconArrow />
              )}
            </li>
            {calculatedShowFirstItem && (
              <>
                <li>
                  {current === 1 ? (
                    <span className={styles.selected}>1</span>
                  ) : (
                    <a href={firstItemHref} {...(title ? { title } : {})}>
                      {1}
                    </a>
                  )}
                </li>
                <li>
                  <a href="#" className={styles.passive}>
                    ...
                  </a>
                </li>
              </>
            )}
            {paginationNumbers?.map((item, index) => {
              const isSelected = current === item || (current === 0 && item === 1);
              const href = pageHref(item);
              const titleAttribute = title && (item === 1 ? title : `${title} ${item}. Sayfa`);

              return (
                <li key={`${id}+${index}`}>
                  {isSelected ? (
                    <span className={styles.selected}>{item}</span>
                  ) : (
                    <a href={href} {...(titleAttribute ? { title: titleAttribute } : {})}>
                      {item}
                    </a>
                  )}
                </li>
              );
            })}
            {calculatedShowLastItem && (
              <>
                <li>
                  <a href="#" className={styles.passive}>
                    ...
                  </a>
                </li>
                <li>
                  {current === maximumPage ? (
                    <span className={styles.selected}>{maximumPage}</span>
                  ) : (
                    <a href={lastItemHref} {...(title ? { title: `${title} ${maximumPage}. Sayfa` } : {})}>
                      {maximumPage}
                    </a>
                  )}
                </li>
              </>
            )}
            <li className={rightArrowClasses}>
              {!isShowLastPage && maximumPage >= 2 ? (
                <a href={nextHref} {...(title ? { title: `${title} ${currentPage + 1}. Sayfa` } : {})}>
                  <IconArrow />
                </a>
              ) : (
                <IconArrow />
              )}
            </li>
          </ul>
          <div className={styles.subText}>{pageCountInfo()}</div>
        </div>
      )}
    </>
  );
};

PaginationWrapper.propTypes = {
  total: PropTypes.array,
  current: PropTypes.number,
  showFirstItem: PropTypes.bool,
  showLastItem: PropTypes.bool,
  maximumPage: PropTypes.number,
  pathName: PropTypes.string,
  listingCount: PropTypes.number,
  url: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isEmlakjetLink: PropTypes.bool,
  totalListingLength: PropTypes.number,
  useLegacyPagination: PropTypes.bool,
  title: PropTypes.string,
};

export default PaginationWrapper;
